/**
 * Co-browse DOM capture — serialize a document into a self-contained, SCRIPT-FREE HTML
 * snapshot the agent's sandboxed viewer can render.
 *
 * Shared by the LOADER (host-page capture in real embeds — it runs in the host page's
 * context, since the widget iframe cannot see the parent DOM) and by the widget app
 * itself (standalone dev harness, where the widget page IS the top document).
 *
 * Safety: every <script> and event handler attribute is stripped, and the viewer renders
 * with a no-scripts sandbox — visitor-page JS never executes on the agent side.
 */

export interface CapturedFrame {
  html: string;
  scrollX: number;
  scrollY: number;
  viewportW: number;
  viewportH: number;
  pageUrl: string;
}

/** Cheap stable hash (djb2) — used to skip shipping an unchanged DOM. */
export function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}

const MAX_HTML_BYTES = 850_000;

/** Serialize `doc` into a sanitized snapshot (null when oversized/unserializable). */
export function captureFrame(doc: Document, win: Window): CapturedFrame | null {
  try {
    const root = doc.documentElement.cloneNode(true) as HTMLElement;

    // Strip anything executable or self-referential.
    root
      .querySelectorAll('script, noscript, iframe, object, embed, link[rel="preload"]')
      .forEach((el) => el.remove());
    // Event-handler attributes (onclick, onload, …) must not survive into the snapshot.
    root.querySelectorAll('*').forEach((el) => {
      for (const attr of [...el.attributes]) {
        if (attr.name.toLowerCase().startsWith('on')) el.removeAttribute(attr.name);
      }
    });

    // Inline live form values (cloneNode copies attributes, not current values).
    const liveInputs = doc.querySelectorAll('input, textarea, select');
    const cloneInputs = root.querySelectorAll('input, textarea, select');
    liveInputs.forEach((live, i) => {
      const clone = cloneInputs[i];
      if (!clone) return;
      if (live instanceof HTMLInputElement && clone instanceof HTMLInputElement) {
        if (live.type === 'password') clone.setAttribute('value', '••••••');
        else if (live.type === 'checkbox' || live.type === 'radio') {
          if (live.checked) clone.setAttribute('checked', '');
          else clone.removeAttribute('checked');
        } else clone.setAttribute('value', live.value);
      } else if (live instanceof HTMLTextAreaElement && clone instanceof HTMLTextAreaElement) {
        clone.textContent = live.value;
      } else if (live instanceof HTMLSelectElement && clone instanceof HTMLSelectElement) {
        clone.querySelectorAll('option').forEach((opt, j) => {
          if (j === live.selectedIndex) opt.setAttribute('selected', '');
          else opt.removeAttribute('selected');
        });
      }
    });

    // Resolve relative URLs (images, stylesheets) against the captured page.
    const base = doc.createElement('base');
    base.setAttribute('href', win.location.href);
    const head = root.querySelector('head');
    head?.insertBefore(base, head.firstChild);

    const html = `<!doctype html>${root.outerHTML}`;
    if (html.length > MAX_HTML_BYTES) return null; // oversized page — skip this frame
    return {
      html,
      scrollX: Math.round(win.scrollX || 0),
      scrollY: Math.round(win.scrollY || 0),
      viewportW: win.innerWidth || 0,
      viewportH: win.innerHeight || 0,
      pageUrl: win.location.href,
    };
  } catch {
    return null;
  }
}
